import { TextArea } from "@certn/cascadia-react";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSaveCannotProvide } from "./hooks";
import { useInformationRequestSaves } from "./InformationRequestSaves";
import { InformationRequestItem, InformationRequestItemAnswer } from "./types";

type Props = {
  caseId: string;
  item: InformationRequestItem;
  onAnswered: (answer: InformationRequestItemAnswer) => void;
  isDisabled?: boolean;
  onDraftChange?: (hasDraft: boolean) => void;
  saveDraftRef?: MutableRefObject<(() => Promise<boolean>) | null>;
};

export function CannotProvideReasonField({
  caseId,
  item,
  onAnswered,
  isDisabled = false,
  onDraftChange,
  saveDraftRef,
}: Props) {
  const { t } = useTranslation();
  const { trackSave } = useInformationRequestSaves();
  const saveCannotProvide = useSaveCannotProvide(caseId, item.id);
  const savedReason = item.cannot_provide
    ? item.cannot_provide_reason ?? ""
    : "";
  const [value, setValue] = useState(savedReason);
  const [isMissing, setIsMissing] = useState(false);
  const lastSavedReason = useRef(savedReason);
  const confirmedReason = useRef(savedReason);
  const lastSave = useRef<Promise<unknown>>(Promise.resolve());
  const failedReason = useRef<string | null>(null);

  const persist = (
    rawValue: string,
    options?: { validateMissing?: boolean }
  ) => {
    if (isDisabled) {
      return Promise.resolve(true);
    }
    const reason = rawValue.trim();
    if (options?.validateMissing) {
      setIsMissing(!reason);
    }
    if (rawValue === lastSavedReason.current) {
      return Promise.resolve(true);
    }
    if (!reason && !lastSavedReason.current) {
      return Promise.resolve(true);
    }
    const newReason = reason ? rawValue : "";
    lastSavedReason.current = newReason;
    const body = reason
      ? { cannot_provide: true, cannot_provide_reason: rawValue }
      : { cannot_provide: false };
    const save = lastSave.current
      .catch(() => undefined)
      .then(() => saveCannotProvide(body));
    lastSave.current = save;
    return trackSave(save).then(
      (answer) => {
        confirmedReason.current = newReason;
        failedReason.current = null;
        if (answer) {
          onAnswered(answer);
        }
        return true;
      },
      () => {
        failedReason.current = rawValue;
        if (lastSavedReason.current === newReason) {
          lastSavedReason.current = confirmedReason.current;
        }
        return false;
      }
    );
  };

  const handleBlur = () => {
    if (isDisabled) {
      return;
    }
    void persist(value, { validateMissing: true });
  };

  if (saveDraftRef) {
    saveDraftRef.current = async () => {
      await lastSave.current.catch(() => undefined);
      if (value.trim() === "") {
        setIsMissing(true);
        return false;
      }
      if (value === confirmedReason.current) {
        return true;
      }
      return persist(value);
    };
  }

  useEffect(() => {
    return () => {
      onDraftChange?.(false);
      if (saveDraftRef) {
        saveDraftRef.current = null;
      }
    };
  }, [onDraftChange, saveDraftRef]);

  return (
    <TextArea
      label={t("Why can't you provide this document?")}
      testID={`information-request-cannot-provide-reason-${item.id}`}
      value={value}
      onChange={(next) => {
        setValue(next);
        onDraftChange?.(next.trim() !== "");
      }}
      onBlur={handleBlur}
      isDisabled={isDisabled}
      minHeight={80}
      isRequired
      isInvalid={isMissing}
      errors={
        isMissing
          ? t("Please give a reason why you can't provide this document.")
          : undefined
      }
    />
  );
}
